// cosmetics.ts — P2.5 mastery-unlock cosmetic LOCKER model: theme-noun-free recolor skins of the
// PLAYER muncher, earned by FREE play milestones across the meta the game already tracks (facts
// learned / boards won / daily streak / Gauntlets threaded). Never bought — the cosmeticIap
// monetization surface stays DARK (guarded OFF via src/config/monetization.ts; cosmeticIapLive()
// below is the plumbing-ready hook and returns false until every compliance interlock is green).
//
// A skin is applied as a Phaser TINT over the animated muncher sheet, so it recolors every frame on
// every surface (board / menu hero / result podium) without forking the art. The P2.3 static skin
// PNGs were retired from runtime: they predate the current one-eyed muncher identity (square,
// two-eyed — see MANIFEST) and swapping them in would break the player-character read. The tint IS
// the skin. Rival is never skinned.
//
// Presentation/meta only: no Core, no network. Versioned localStorage exactly like stats.ts
// (tolerant of blocked storage), migrating the P2.3 'trivia.cosmetic.v1' equipped key and
// grandfathering any skin the old facts-only thresholds had already unlocked, so nobody loses an
// earned skin when the milestones diversify.
//
// Theme-noun-free doctrine (style bible is FROZEN): cosmetics only change COLOR — never the subject —
// so the avatar never implies a food/animal/character/brand.

import { factsLearnedTotal } from './mastery';
import { getStats } from './stats';
import { isSurfaceLive } from '../config/monetization';

/** Which already-tracked meta signal a milestone reads (never Core; renderer meta only). */
export type MilestoneKind = 'facts' | 'wins' | 'dailyStreak' | 'gauntlets';

export interface Milestone {
  kind: MilestoneKind;
  target: number;
}

export interface Cosmetic {
  id: string;
  label: string;
  /** The skin's NAMED color (0xRRGGBB): swatches, glows, geometric-muncher fills, and the raster
   *  multiply-tint fallback. null = original art, no recolor. */
  tint: number | null;
  /** Hue rotation (degrees) that turns the CYAN base sheet into this skin's named hue — the WebGL
   *  ColorMatrix path (muncher.applyCosmeticSkin). Required because the multiplicative tint can't
   *  produce warm hues over a cyan base (gold would render green). */
  hue?: number;
  /** Optional saturation adjust (-1..1) layered onto the hue rotation (slate is a desaturated look). */
  sat?: number;
  /** The free unlock milestone. null = always unlocked (the default identity skin). */
  milestone: Milestone | null;
}

// Ordered easiest → hardest. 'cyan' is the untouched shipped art (the identity default). One lane
// per meta system so the locker rewards learning, winning, showing up, and the signature verb.
// Hue values are rotations from the base body cyan (~193°) to each skin's named hue.
export const COSMETICS: Cosmetic[] = [
  { id: 'cyan', label: 'Classic Cyan', tint: null, milestone: null },
  { id: 'slate', label: 'Cool Slate', tint: 0x94a3b8, hue: 19, sat: -0.5, milestone: { kind: 'facts', target: 3 } },
  { id: 'gold', label: 'Molten Gold', tint: 0xffd166, hue: -150, milestone: { kind: 'wins', target: 10 } },
  { id: 'emerald', label: 'Emerald', tint: 0x34d399, hue: -35, milestone: { kind: 'facts', target: 25 } },
  { id: 'violet', label: 'Royal Violet', tint: 0xa78bfa, hue: 59, milestone: { kind: 'dailyStreak', target: 3 } },
  { id: 'crimson', label: 'Crimson', tint: 0xf87171, hue: 167, milestone: { kind: 'gauntlets', target: 25 } },
];

export function cosmeticById(id: string): Cosmetic {
  return COSMETICS.find((c) => c.id === id) ?? COSMETICS[0];
}

// ---- persistence (versioned, tolerant — the stats.ts pattern) ---------------

const KEY = 'trivia.cosmetics.v2';
const LEGACY_KEY = 'trivia.cosmetic.v1';
// P2.3 facts-only thresholds — read ONCE at migration so an already-earned skin stays earned even
// though its live milestone changed (e.g. gold moved from 10 facts to 10 wins).
const LEGACY_FACTS: Record<string, number> = { cyan: 0, slate: 3, gold: 10, emerald: 25, violet: 50, crimson: 100 };

interface CosmeticsSave {
  equipped: string;
  /** ids whose milestone has been met (unlocks are permanent — a wiped signal never re-locks). */
  unlocked: string[];
  /** ids whose "SKIN UNLOCKED!" moment has already been shown (each celebrates exactly once). */
  celebrated: string[];
}

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function persist(s: CosmeticsSave): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(s));
    window.localStorage.removeItem(LEGACY_KEY);
  } catch {
    // Storage can be blocked (private mode); cosmetics are best-effort, never load-bearing.
  }
}

/** First run on the v2 schema: carry over the P2.3 equipped id + grandfather old facts unlocks. */
function migrate(store: Storage | null): CosmeticsSave {
  const equipped = store?.getItem(LEGACY_KEY) ?? 'cyan';
  const facts = factsLearnedTotal();
  const owned = COSMETICS.filter((c) => facts >= (LEGACY_FACTS[c.id] ?? 0)).map((c) => c.id);
  // Grandfathered skins are marked celebrated too — no retroactive toast spam on upgrade.
  const save: CosmeticsSave = { equipped, unlocked: owned, celebrated: [...owned] };
  persist(save);
  return save;
}

function readSave(): CosmeticsSave {
  const store = storage();
  let raw: string | null = null;
  try {
    raw = store?.getItem(KEY) ?? null;
  } catch {
    raw = null;
  }
  if (!raw) return migrate(store);
  try {
    const p = JSON.parse(raw) as Partial<CosmeticsSave>;
    const ids = new Set(COSMETICS.map((c) => c.id));
    const clean = (a: unknown): string[] =>
      Array.isArray(a) ? a.filter((v): v is string => typeof v === 'string' && ids.has(v)) : [];
    return {
      equipped: typeof p.equipped === 'string' && ids.has(p.equipped) ? p.equipped : 'cyan',
      unlocked: clean(p.unlocked),
      celebrated: clean(p.celebrated),
    };
  } catch {
    return { equipped: 'cyan', unlocked: [], celebrated: [] };
  }
}

// ---- milestone progress ------------------------------------------------------

/** Live value of a milestone signal, read from the EXISTING tracked meta. `bestDailyStreak` (not the
 *  live streak) backs the streak lane so unlock progress never moves backwards. */
export function milestoneCurrent(kind: MilestoneKind): number {
  switch (kind) {
    case 'facts':
      return factsLearnedTotal();
    case 'wins':
      return getStats().wins;
    case 'dailyStreak':
      return getStats().bestDailyStreak;
    case 'gauntlets':
      return getStats().gauntletsThreaded;
  }
}

const UNIT: Record<MilestoneKind, string> = { facts: 'facts', wins: 'wins', dailyStreak: 'days', gauntlets: 'threads' };

export interface MilestoneProgress {
  cur: number;
  target: number;
  /** 0..1 toward the target (clamped) */
  frac: number;
  done: boolean;
  /** the milestone as a goal sentence, e.g. "Win 10 boards" */
  goal: string;
  /** exact progress, e.g. "4/10 wins" */
  progress: string;
  /** distance left, e.g. "6 wins to go" */
  remaining: string;
}

/** Progress toward a cosmetic's milestone (null for the always-unlocked default). */
export function cosmeticProgress(c: Cosmetic): MilestoneProgress | null {
  if (!c.milestone) return null;
  const { kind, target } = c.milestone;
  const cur = Math.max(0, Math.min(milestoneCurrent(kind), target));
  const left = target - cur;
  const unit = UNIT[kind];
  const one = unit.slice(0, -1);
  const goal =
    kind === 'facts'
      ? `Learn ${target} facts`
      : kind === 'wins'
        ? `Win ${target} boards`
        : kind === 'dailyStreak'
          ? `Reach a ${target}-day Daily streak`
          : `Thread ${target} Gauntlets`;
  return {
    cur,
    target,
    frac: cur / target,
    done: cur >= target,
    goal,
    progress: `${cur}/${target} ${unit}`,
    remaining: `${left} ${left === 1 ? one : unit} to go`,
  };
}

// ---- unlock / equip ----------------------------------------------------------

export function isCosmeticUnlocked(c: Cosmetic, save: CosmeticsSave = readSave()): boolean {
  if (!c.milestone) return true;
  if (save.unlocked.includes(c.id)) return true;
  const p = cosmeticProgress(c);
  return p !== null && p.done;
}

/** The cosmetic the player has equipped — but never a LOCKED one (e.g. if local meta was wiped). */
export function selectedCosmetic(): Cosmetic {
  const save = readSave();
  const c = cosmeticById(save.equipped);
  return isCosmeticUnlocked(c, save) ? c : COSMETICS[0];
}

/** Equip a cosmetic. Returns false (and changes nothing) if it isn't unlocked yet. */
export function selectCosmetic(id: string): boolean {
  const save = readSave();
  const c = cosmeticById(id);
  if (!isCosmeticUnlocked(c, save)) return false;
  save.equipped = c.id;
  // Equipping a live-derived unlock also pins it into the permanent set.
  if (c.milestone && !save.unlocked.includes(c.id)) save.unlocked.push(c.id);
  persist(save);
  return true;
}

/** How many cosmetics are unlocked right now (for the "N/M unlocked" locker header). */
export function unlockedCosmeticCount(save: CosmeticsSave = readSave()): number {
  return COSMETICS.reduce((n, c) => (isCosmeticUnlocked(c, save) ? n + 1 : n), 0);
}

/** The locked cosmetic the player is CLOSEST to (max progress fraction) — the "keep playing" nudge. */
export function nextCosmeticGoal(): { cosmetic: Cosmetic; progress: MilestoneProgress } | null {
  const save = readSave();
  let best: { cosmetic: Cosmetic; progress: MilestoneProgress } | null = null;
  for (const c of COSMETICS) {
    if (isCosmeticUnlocked(c, save)) continue;
    const p = cosmeticProgress(c);
    if (p && (!best || p.frac > best.progress.frac)) best = { cosmetic: c, progress: p };
  }
  return best;
}

// Session guard so blocked storage can't replay the same toast on every menu visit.
const sessionCelebrated = new Set<string>();

/**
 * Consume pending unlock celebrations: skins whose milestone is now met but whose "SKIN UNLOCKED!"
 * moment hasn't been shown. Persists both the unlock and the celebrated mark so each skin toasts
 * exactly once, ever. Single celebration site: returning-hub menu enter (MenuScene).
 */
export function takeUnlockCelebrations(): Cosmetic[] {
  const save = readSave();
  const fresh: Cosmetic[] = [];
  for (const c of COSMETICS) {
    if (!c.milestone || save.celebrated.includes(c.id) || sessionCelebrated.has(c.id)) continue;
    if (!isCosmeticUnlocked(c, save)) continue;
    if (!save.unlocked.includes(c.id)) save.unlocked.push(c.id);
    save.celebrated.push(c.id);
    sessionCelebrated.add(c.id);
    fresh.push(c);
  }
  if (fresh.length > 0) persist(save);
  return fresh;
}

/**
 * P2.5 dark-flag hook: true only when the cosmeticIap surface is LIVE (master switch + flag + every
 * compliance interlock green — see src/config/monetization.ts and docs/MONETIZATION.md). Ships
 * FALSE; the locker shows only the free milestone lanes plus the disabled store teaser until then.
 * There is NO purchase path in this module either way.
 */
export function cosmeticIapLive(): boolean {
  return isSurfaceLive('cosmeticIap');
}
